import React, { useState } from "react";
import {
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  Divider,
  Container,
  Stack,
  Typography,
  Tooltip,
  Alert,
} from "@mui/material";
import QuizUpload from "./QuizUpload";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import DescriptionIcon from "@mui/icons-material/Description";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import AssignmentIcon from "@mui/icons-material/Assignment";
import InfoIcon from "@mui/icons-material/Info";

const LabList = ({ ...rest }) => {
  const [submitQuestions, setSubmitQuestions] = useState({
    data: []
  });

  const handleClickDownload = () => {
    const link = document.createElement('a');
    link.href = 'https://labasservice-assessment.s3.ap-south-1.amazonaws.com/samplequestions.xlsx';
    link.download = 'sampleQuestions.xlsx';
    link.click();
  };

  return (
    <Container maxWidth={false} sx={{ py: 6, pb: 8, px: { xs: 2, sm: 3, md: 4, lg: 5, xl: 6 } }}>
      <Card {...rest} sx={{ mb: 4 }}>
        {/* Hero Header */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, rgba(46,125,50,0.12), rgba(21,101,192,0.12))',
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
            p: { xs: 2.5, md: 3 },
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <AssignmentIcon color="primary" sx={{ fontSize: 32 }} />
            <Box>
              <Typography variant="h4" component="h1">
                Create New Assessment
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Download the template, fill in your questions, and upload to create a new assessment
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Divider />

        {/* Main Content */}
        <Box sx={{ p: { xs: 2.5, md: 3 } }}>
          <Grid
            container
            spacing={{ xs: 3, sm: 4 }}
            sx={{ alignItems: 'stretch' }}
          >
            {/* Download Template Card */}
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    boxShadow: 6,
                    transform: 'translateY(-4px)',
                  },
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 3 }}>
                  <Stack spacing={2} alignItems="center" sx={{ flexGrow: 1 }}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: '50%',
                        backgroundColor: 'primary.light',
                        color: 'primary.contrastText',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <DescriptionIcon sx={{ fontSize: 40 }} />
                    </Box>
                    <Typography variant="h5" align="center" sx={{ fontWeight: 600 }}>
                      Download Assessment Template
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      align="center"
                      sx={{ maxWidth: 300 }}
                    >
                      Get started by downloading our Excel template. Fill in your questions and options following the provided format.
                    </Typography>
                    <Box sx={{ mt: 'auto', pt: 2 }}>
                      <Tooltip title="Download the assessment template file" arrow>
                        <Button
                          variant="contained"
                          onClick={handleClickDownload}
                          startIcon={<CloudDownloadIcon />}
                          sx={{
                            borderRadius: '50px',
                            px: 4,
                            py: 1.5,
                            fontSize: '1rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            boxShadow: 2,
                            '&:hover': {
                              boxShadow: 4,
                            },
                          }}
                          aria-label="Download assessment template"
                        >
                          Download Template
                        </Button>
                      </Tooltip>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            {/* Upload Assessment Card */}
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    boxShadow: 6,
                    transform: 'translateY(-4px)',
                  },
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 3 }}>
                  <Stack spacing={2} alignItems="center" sx={{ flexGrow: 1 }}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: '50%',
                        backgroundColor: 'success.light',
                        color: 'success.contrastText',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <CloudUploadIcon sx={{ fontSize: 40 }} />
                    </Box>
                    <Typography variant="h5" align="center" sx={{ fontWeight: 600 }}>
                      Upload Assessment
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      align="center"
                      sx={{ maxWidth: 300 }}
                    >
                      Once you've filled in the template, upload your completed file to create the assessment. Supported formats: .xlsx, .xls, .csv
                    </Typography>
                    <Box sx={{ mt: 'auto', pt: 2, width: '100%', display: 'flex', justifyContent: 'center' }}>
                      <QuizUpload
                        setSubmitQuestions={setSubmitQuestions}
                        submitQuestions={submitQuestions}
                      />
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Help Information */}
          <Alert
            icon={<InfoIcon />}
            severity="info"
            sx={{
              mt: 4,
              borderRadius: 2,
              '& .MuiAlert-message': {
                width: '100%',
              },
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Getting Started
            </Typography>
            <Typography variant="body2" component="div">
              <strong>Step 1:</strong> Download the assessment template using the button above.
              <br />
              <strong>Step 2:</strong> Open the template in Excel and fill in your questions, options, and correct answers.
              <br />
              <strong>Step 3:</strong> Save your file and upload it using the "Create Assessment" button.
              <br />
              <strong>Note:</strong> Make sure to follow the template format exactly to ensure successful upload.
            </Typography>
          </Alert>
        </Box>
      </Card>
    </Container>
  );
};

export default LabList;
